/**
 * Availability hook
 * TanStack Query hooks for user availability management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAvailability,
  bulkUpdateAvailability,
  getAvailabilitySummary,
  setAvailability,
} from '@/lib/api/availability';
import type {
  GetAvailabilityQuery,
  BulkAvailabilityRequest,
  GetAvailabilitySummaryQuery,
} from '@/types/api';
import { availabilityKeys } from '@/lib/query-keys';

// Re-export for backwards compatibility
export { availabilityKeys };

/**
 * Hook to fetch user availability for a date range
 *
 * Features:
 * - Get availability for specific date range
 * - Filter by user and meal type
 * - Useful for displaying user's week calendar
 *
 * @example
 * ```tsx
 * function AvailabilityCalendar({ userId }: { userId: number }) {
 *   const { data, isLoading } = useAvailability({
 *     userId,
 *     startDate: '2025-01-01',
 *     endDate: '2025-01-07',
 *   });
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       {data.availability.map(avail => (
 *         <div key={avail.id}>
 *           {avail.date} - {avail.mealType}: {avail.isPresent ? 'Present' : 'Absent'}
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useAvailability(params?: GetAvailabilityQuery) {
  return useQuery({
    queryKey: availabilityKeys.list(params),
    queryFn: () => getAvailability(params),
    enabled: !!(params?.startDate && params?.endDate),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
}

/**
 * Hook to bulk update user availability
 *
 * Features:
 * - Update multiple days/meals at once
 * - Optimistic updates for better UX
 * - Automatic cache invalidation
 * - Useful for weekly availability updates
 *
 * @example
 * ```tsx
 * function WeekCalendar({ userId }: { userId: number }) {
 *   const bulkUpdateMutation = useBulkUpdateAvailability();
 *
 *   const handleSave = (weekData: BulkAvailabilityItem[]) => {
 *     bulkUpdateMutation.mutate({
 *       userId,
 *       availability: weekData,
 *     });
 *   };
 *
 *   return (
 *     <button
 *       onClick={() => handleSave(weekData)}
 *       disabled={bulkUpdateMutation.isPending}
 *     >
 *       {bulkUpdateMutation.isPending ? 'Saving...' : 'Save'}
 *     </button>
 *   );
 * }
 * ```
 */
export function useBulkUpdateAvailability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BulkAvailabilityRequest) => bulkUpdateAvailability(data),
    onSuccess: () => {
      // Invalidate all availability queries for this user
      queryClient.invalidateQueries({
        queryKey: availabilityKeys.all,
      });
    },
  });
}

/**
 * Hook to fetch team availability summary (organizers only)
 *
 * Features:
 * - Get summary of all users' availability for date range
 * - See who's present for each meal
 * - Useful for menu planning
 *
 * @example
 * ```tsx
 * function TeamAvailabilityView() {
 *   const { data, isLoading } = useAvailabilitySummary({
 *     startDate: '2025-01-01',
 *     endDate: '2025-01-07',
 *   });
 *
 *   if (isLoading) return <Spinner />;
 *
 *   return (
 *     <div>
 *       {data.summary.map(day => (
 *         <div key={`${day.date}-${day.mealType}`}>
 *           {day.date} {day.mealType}: {day.presentUsers.length}/{day.totalUsers}
 *         </div>
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useAvailabilitySummary(params?: GetAvailabilitySummaryQuery) {
  return useQuery({
    queryKey: availabilityKeys.summary(params),
    queryFn: () => getAvailabilitySummary(params!),
    enabled: !!(params?.startDate && params?.endDate),
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
}

/**
 * Hook to update a single user's availability
 *
 * Features:
 * - Update one meal's availability
 * - Automatic cache invalidation
 * - Useful for organizers editing team availability
 *
 * @example
 * ```tsx
 * function AvailabilityCell({ userId, date, mealType }) {
 *   const mutation = useSetAvailability();
 *
 *   const handleToggle = (isPresent: boolean) => {
 *     mutation.mutate({ userId, date, mealType, isPresent });
 *   };
 *
 *   return (
 *     <button onClick={() => handleToggle(true)} disabled={mutation.isPending}>
 *       {mutation.isPending ? '...' : '✓'}
 *     </button>
 *   );
 * }
 * ```
 */
export function useSetAvailability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: {
      userId: number;
      date: string;
      mealType: 'lunch' | 'dinner';
      isPresent: boolean;
    }) => setAvailability(data),
    onSuccess: () => {
      // Invalidate all availability queries to refresh data
      queryClient.invalidateQueries({
        queryKey: availabilityKeys.all,
      });
    },
  });
}
